import { api } from ".";
import {
  IChat,
  ISendMessageRequest,
  ISendMessageResponse,
  IOpenAiChatModel,
  ICreateModelRequest,
  IUpdateModelRequest,
  IListFilesResponse,
  IGetFileResponse,
  IUploadFileRequest,
  IUpdateFileRequest,
  IOpenAiFile,
  IPaginatedChatsResponse,
  IListChatsParams,
} from "../../types/Chat";

const chatQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    listChats: builder.query<
      IChat[],
      {
        accountId?: string;
        chatType?: "PatientChatBot" | "ContractManagementBot";
        orderExternalId?: string;
      }
    >({
      query: ({ accountId, chatType, orderExternalId }) => {
        const params = new URLSearchParams();
        if (accountId) params.append("accountId", accountId);
        if (chatType) params.append("chatType", chatType);
        if (orderExternalId) params.append("orderExternalId", orderExternalId);

        return {
          url: `chats?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response: { data: IChat[] }) => response.data,
    }),
    listAllChats: builder.query<
      IPaginatedChatsResponse,
      IListChatsParams | void
    >({
      query: (params) => {
        const { page = 1, limit = 25, modelId, accountId } = params || {};
        const searchParams = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        if (modelId) {
          searchParams.append("modelId", modelId.toString());
        }

        if (accountId) {
          searchParams.append("accountId", accountId);
        }

        return {
          url: `chats/all?${searchParams.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response: { data: IPaginatedChatsResponse }) =>
        response.data,
      providesTags: ["Chat"],
    }),
    getChat: builder.query<IChat, string>({
      query: (chatId) => ({
        url: `chats/${chatId}`,
        method: "GET",
      }),
      transformResponse: (response: { data: IChat }) => response.data,
      providesTags: (result, error, id) => [{ type: "Chat", id }],
    }),
    sendMessage: builder.mutation<ISendMessageResponse, ISendMessageRequest>({
      query: (messageData) => ({
        url: "chats/send_message",
        method: "POST",
        body: messageData,
      }),
      transformResponse: (response: { data: ISendMessageResponse }) =>
        response.data,
    }),
    listModels: builder.query<IOpenAiChatModel[], void>({
      query: () => ({
        url: "chats/models",
        method: "GET",
      }),
      transformResponse: (response: { data: IOpenAiChatModel[] }) =>
        response.data,
      providesTags: ["ChatModel"],
    }),
    getModel: builder.query<IOpenAiChatModel, number>({
      query: (modelId) => ({
        url: `chats/models/${modelId}`,
        method: "GET",
      }),
      transformResponse: (response: { data: IOpenAiChatModel }) =>
        response.data,
      providesTags: (result, error, id) => [{ type: "ChatModel", id }],
    }),
    createModel: builder.mutation<IOpenAiChatModel, ICreateModelRequest>({
      query: (modelData) => ({
        url: "chats/models",
        method: "POST",
        body: modelData,
      }),
      transformResponse: (response: { data: IOpenAiChatModel }) =>
        response.data,
      invalidatesTags: ["ChatModel"],
    }),
    updateModel: builder.mutation<
      IOpenAiChatModel,
      { id: number; data: IUpdateModelRequest }
    >({
      query: ({ id, data }) => ({
        url: `chats/models/${id}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: (response: { data: IOpenAiChatModel }) =>
        response.data,
      invalidatesTags: (result, error, { id }) => [
        { type: "ChatModel", id },
        "ChatModel",
      ],
    }),
    listFiles: builder.query<IListFilesResponse, void>({
      query: () => ({
        url: "chats/files",
        method: "GET",
      }),
      transformResponse: (response: { data: IListFilesResponse }) =>
        response.data,
      providesTags: ["ChatFile"],
    }),
    getFile: builder.query<IGetFileResponse, string>({
      query: (fileId) => ({
        url: `chats/files/${fileId}`,
        method: "GET",
      }),
      transformResponse: (response: { data: IGetFileResponse }) =>
        response.data,
      providesTags: (result, error, id) => [{ type: "ChatFile", id }],
    }),
    uploadFile: builder.mutation<IOpenAiFile, IUploadFileRequest>({
      query: (fileData) => ({
        url: "chats/files/upload",
        method: "POST",
        body: fileData,
      }),
      transformResponse: (response: { data: IOpenAiFile }) => response.data,
      invalidatesTags: ["ChatFile"],
    }),
    deleteFile: builder.mutation<void, string>({
      query: (fileId) => ({
        url: `chats/files/${fileId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ChatFile"],
    }),
    updateFile: builder.mutation<
      IOpenAiFile,
      { fileId: string; data: IUpdateFileRequest }
    >({
      query: ({ fileId, data }) => ({
        url: `chats/files/${fileId}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: (response: { data: IOpenAiFile }) => response.data,
      invalidatesTags: (result, error, { fileId }) => [
        { type: "ChatFile", id: fileId },
        "ChatFile",
        "ChatModel",
      ],
    }),
  }),
});

export const {
  useListChatsQuery,
  useListAllChatsQuery,
  useSendMessageMutation,
  useListModelsQuery,
  useGetModelQuery,
  useCreateModelMutation,
  useUpdateModelMutation,
  useListFilesQuery,
  useGetFileQuery,
  useUploadFileMutation,
  useUpdateFileMutation,
  useDeleteFileMutation,
  useGetChatQuery,
} = chatQueries;
